import { ChangeEvent, MouseEvent } from "react";
import Alert from "@mui/material/Alert";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { NoteReasonType } from "../../types/credit-debit-note.type";
import {
  CREDIT_REASON_TYPES,
  ETAX_REASON_LABEL,
  NOTE_REASON_TYPE_LABEL,
  OTHER_ETAX_REASON_CODES,
  STOCK_RETURN_REASON_TYPE,
} from "./constants";
import { NoteDraft, NoteFormErrors, etaxReasonCodesFor, noteReasonType } from "./noteDraft";

interface NoteReasonSectionProps {
  draft: NoteDraft;
  errors: NoteFormErrors;
  onChange: (patch: Partial<NoteDraft>) => void;
}

const etaxHelperTextOf = (
  error: string | undefined,
  hasCodes: boolean,
  isOtherCode: boolean,
): string | undefined => {
  if (error) return error;
  if (!hasCodes) return "เลือกประเภทเหตุผลก่อน";
  return isOtherCode ? "รหัสอื่นๆ: ข้อความสาเหตุด้านล่างจะส่งเป็นเหตุผลใน e-Tax" : undefined;
};

/**
 * What the note corrects and the e-Tax purpose code that goes with it. A credit note picks its
 * reason — only คืนสินค้า puts goods back into van stock; a debit note always re-prices. The
 * codes offered follow the note type and the reason (handbook §7.2).
 */
export default function NoteReasonSection({
  draft,
  errors,
  onChange,
}: Readonly<NoteReasonSectionProps>) {
  const reasonType = noteReasonType(draft);
  const etaxCodes = etaxReasonCodesFor(draft.noteType, reasonType);
  const isCreditNote = draft.noteType === "CREDIT";
  const isStockReturn = isCreditNote && reasonType === STOCK_RETURN_REASON_TYPE;
  // Only a shop in the system leads to a route; a typed buyer has none to take the goods.
  const hasNoShop = draft.invoiceSource === "MANUAL" && draft.buyerSource === "TYPED";
  const isOtherCode =
    draft.etaxReasonCode !== "" && OTHER_ETAX_REASON_CODES.includes(draft.etaxReasonCode);
  const hasEtaxCodes = etaxCodes.length > 0;
  const etaxHelperText = etaxHelperTextOf(errors.etaxReasonCode, hasEtaxCodes, isOtherCode);
  const showsNoShopWarning = isStockReturn && hasNoShop;
  const showsStockReturnInfo = isStockReturn && !hasNoShop;
  const noStockCaption =
    isCreditNote && reasonType !== "" && !isStockReturn
      ? `${NOTE_REASON_TYPE_LABEL[reasonType]}: ไม่คืนสินค้าเข้าสต็อก`
      : "";

  const handleReasonTypeChange = (_event: MouseEvent<HTMLElement>, value: NoteReasonType | null) => {
    if (value) onChange({ reasonType: value });
  };
  const handleEtaxCodeChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ etaxReasonCode: etaxCodes.find((code) => code === event.target.value) ?? "" });

  return (
    <Stack gap={2}>
      {isCreditNote ? (
        <Stack>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={draft.reasonType || null}
            onChange={handleReasonTypeChange}
          >
            {CREDIT_REASON_TYPES.map((option) => (
              <ToggleButton key={option} value={option} sx={{ px: 2 }}>
                {NOTE_REASON_TYPE_LABEL[option]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {errors.reasonType && <FormHelperText error>{errors.reasonType}</FormHelperText>}
        </Stack>
      ) : (
        <Typography variant="body2">
          ใบเพิ่มหนี้: {NOTE_REASON_TYPE_LABEL.PRICE} (ไม่มีผลกับสต็อก)
        </Typography>
      )}

      <TextField
        select
        fullWidth
        size="small"
        required
        label="รหัสเหตุผล e-Tax"
        value={draft.etaxReasonCode}
        disabled={!hasEtaxCodes}
        onChange={handleEtaxCodeChange}
        error={Boolean(errors.etaxReasonCode)}
        helperText={etaxHelperText}
      >
        {etaxCodes.map((code) => (
          <MenuItem key={code} value={code}>
            {code} {ETAX_REASON_LABEL[code]}
          </MenuItem>
        ))}
      </TextField>

      {showsNoShopWarning && (
        <Alert severity="warning">
          ผู้ซื้อที่กรอกเองไม่มีรหัสร้านค้า ใบนี้จะไม่คืนสินค้าเข้าสต็อก
        </Alert>
      )}
      {showsStockReturnInfo && (
        <Alert severity="info">
          สินค้าที่คืนจะกลับเข้าสต็อกบนรถของสายที่ร้านค้าผูกอยู่ในวันที่เอกสาร
          (ดูสายที่รับคืนได้ในหน้ารายละเอียดหลังออกเอกสาร) ถ้าวันนั้นสายไม่มีสต็อก จะออกเอกสารไม่ได้
        </Alert>
      )}
      {noStockCaption && (
        <Typography variant="caption" color="text.secondary">
          {noStockCaption}
        </Typography>
      )}
    </Stack>
  );
}
